/**
 * Standard License & Data-Usage Agreement — shared viewer.
 *
 * The agreement text lives on the backend (see
 * api_backend_fastify_server/src/constants/legal-agreement.ts) and is served
 * by `GET /api/auth/legal/agreement`, so the client never bundles its own
 * copy. `useLegalAgreement()` fetches and caches it; `LegalAgreementBody`
 * renders the loading / error / content states; `LegalAgreementDialog` is a
 * plain read-only modal used by the signup form's "read the agreement" link.
 *
 * The blocking, must-accept variant lives in
 * `components/auth/TermsAcceptanceGate.tsx` and reuses `LegalAgreementBody`.
 */

import { useQuery } from '@tanstack/react-query';
import { Loader2, FileText } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { authService, type LegalAgreement } from '@/services/auth.service';

/** React Query key for the current legal agreement. */
export const legalAgreementQueryKey = ['legal', 'agreement'] as const;

/**
 * Fetch + cache the current Standard License & Data-Usage Agreement.
 * It changes rarely, so keep it fresh for a long time. Pass `enabled=false`
 * to defer the request (e.g. the post-login gate only needs it when shown).
 */
export function useLegalAgreement(enabled = true) {
  return useQuery<LegalAgreement>({
    queryKey: legalAgreementQueryKey,
    queryFn: () => authService.getLegalAgreement(),
    enabled,
    staleTime: 60 * 60 * 1000, // 1 hour
    gcTime: 24 * 60 * 60 * 1000,
  });
}

/** Splits the plain-text agreement into paragraphs on blank lines. */
function toParagraphs(content: string): string[] {
  return content
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);
}

interface LegalAgreementBodyProps {
  agreement?: LegalAgreement;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Renders the agreement text (or its loading / error state) inside a
 * scrollable region. Used by both the read-only dialog and the blocking gate.
 */
export function LegalAgreementBody({ agreement, isLoading, isError }: LegalAgreementBodyProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading agreement…
      </div>
    );
  }

  if (isError || !agreement) {
    return (
      <div className="py-8 text-sm text-destructive">
        Couldn’t load the agreement. Please check your connection and try again.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        Version {agreement.version} · Effective {agreement.effectiveDate}
      </p>
      <div className="max-h-[50vh] overflow-y-auto rounded-md border bg-muted/30 p-4 text-sm leading-relaxed">
        {toParagraphs(agreement.content).map((para, i) => (
          <p key={i} className="mb-3 last:mb-0">
            {para}
          </p>
        ))}
      </div>
    </div>
  );
}

interface LegalAgreementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Read-only agreement viewer. Opened from the signup form's
 * "Standard License & Data-Usage Agreement" link — purely informational,
 * dismissable with the X / Close button / Escape / backdrop.
 */
export function LegalAgreementDialog({ open, onOpenChange }: LegalAgreementDialogProps) {
  const { data, isLoading, isError } = useLegalAgreement();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <div className="flex items-center gap-2 text-primary">
            <FileText className="h-4 w-4" />
            <DialogTitle>{data?.title ?? 'Standard License & Data-Usage Agreement'}</DialogTitle>
          </div>
          <DialogDescription>
            Please review the agreement below. You’ll be asked to accept it when you sign up.
          </DialogDescription>
        </DialogHeader>

        <LegalAgreementBody agreement={data} isLoading={isLoading} isError={isError} />

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
